import React, { useEffect, useState } from "react";
import { Navigation, type NavigationProps } from "../navigation/navigation";
import { Footer, type FooterProps } from "../footer/footer";
import { type BaseComponentProps } from "../../types/puck";

export interface ComponentRefProps extends BaseComponentProps {
  componentId: string;
  componentType: "navigation" | "footer";
}

interface SiteComponent {
  id: string;
  type: string;
  name: string;
  content: NavigationProps | FooterProps;
}

/**
 * ComponentRef - Dynamically renders a SiteComponent by its UUID
 *
 * This component fetches a SiteComponent from the API and renders
 * the appropriate component (Navigation or Footer) with the fetched data.
 *
 * This allows pages to reference shared components instead of embedding
 * the full component data, making global edits propagate automatically.
 */
export const ComponentRef: React.FC<ComponentRefProps> = ({
  componentId,
  componentType,
  className,
}) => {
  const [component, setComponent] = useState<SiteComponent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!componentId) {
      setLoading(false);
      setError("No component ID provided");
      return;
    }

    const fetchComponent = async () => {
      try {
        // Get config from global window object
        const config = (window as any).__SERVICEPRO_CONFIG__;
        if (!config?.siteId) {
          setError("Site configuration not found");
          setLoading(false);
          return;
        }

        const apiUrl = config.apiUrl || "";
        const token = config.token || localStorage.getItem("token");

        const response = await fetch(
          `${apiUrl}/api/sites/${config.siteId}/components/${componentId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch component: ${response.status}`);
        }

        const data = await response.json();
        setComponent(data);
        setError(null);
      } catch (err: any) {
        console.error("ComponentRef fetch error:", err);
        setError(err.message || "Failed to load component");
      } finally {
        setLoading(false);
      }
    };

    fetchComponent();
  }, [componentId]);

  // Loading state
  if (loading) {
    return (
      <div className={`jobsite:animate-pulse jobsite:bg-muted ${className || ""}`}>
        {componentType === "navigation" && <div className="jobsite:h-20 jobsite:w-full" />}
        {componentType === "footer" && <div className="jobsite:h-64 jobsite:w-full" />}
      </div>
    );
  }

  // Error state (show placeholder in editor, hide in production)
  if (error || !component) {
    const isEditor = typeof window !== "undefined" && (window as any).__SERVICEPRO_CONFIG__?.token;
    if (!isEditor) return null;

    return (
      <div
        className={`jobsite:p-4 jobsite:text-center jobsite:text-muted-foreground jobsite:bg-muted/50 jobsite:border jobsite:border-dashed jobsite:border-border ${className || ""}`}
      >
        <p className="jobsite:text-sm">{error || `${componentType} component not found`}</p>
        <p className="jobsite:text-xs jobsite:mt-1">ID: {componentId}</p>
      </div>
    );
  }

  // Render the appropriate component
  if (componentType === "navigation" && component.content) {
    return (
      <Navigation
        id={`component-ref-${componentId}`}
        className={className}
        {...(component.content as NavigationProps)}
      />
    );
  }

  if (componentType === "footer" && component.content) {
    return (
      <Footer
        id={`component-ref-${componentId}`}
        className={className}
        {...(component.content as FooterProps)}
      />
    );
  }

  return null;
};
